import React from 'react';

import { Anchor, Stack, Text } from '@mantine/core';
import { useInView } from 'react-intersection-observer';

import { useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';
import { activeEnrollmentDevices } from '@/utils/enrollmentChecklist/enrollmentSettings';

import { DeviceHoverSource } from '@/pages/physician/TasksPage/TaskDetailPage/deviceHover';
import { DeviceHoverCard } from '@/pages/physician/TasksPage/TaskDetailPage/DeviceHoverCard';
import { MatchDevice } from '@/types/matchDevice';
import { PatientData } from '@/types/patientList';

import styles from './cellsV2.module.scss';
import { vendorDisplayName } from './ReportName';

interface DeviceCellProps {
  patient: PatientData;
}

const POC_YELLOW = '#d9a206';

function chartUrl(patientId: string, tab: 'devices' | 'insurance') {
  return `/physician/patients/${patientId}?tab=${tab}`;
}

export function openPatientChart(patientId: string, tab: 'devices' | 'insurance') {
  window.open(chartUrl(patientId, tab), '_blank', 'noopener,noreferrer');
}

function hoverSource(device: MatchDevice): DeviceHoverSource {
  return {
    id: device.id,
    isEriTracked: device.isEriTracked,
    vendor: device.vendor,
    type: device.type,
    modelName: device.modelName,
    modelNumber: device.modelNumber,
    serialNumber: device.serialNumber,
    implantDate: device.implantDate,
    deviceImplantDate: device.deviceImplantDate,
    connectivityStatus: device.connectivityStatus,
    deviceTelemetry: device.deviceTelemetry,
    lastTransmissionAt: device.lastTransmissionAt,
    nextTransmissionAt: device.nextTransmissionAt
  };
}

/** Chart device. Hover matches a transmission-tracking task. No active device opens the chart. */
export const DeviceCell: React.FC<DeviceCellProps> = ({ patient }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0 });
  const { data: full } = useGetPatientQuery(patient.id, {
    skip: !patient.id || !inView
  });
  const activeDevices = activeEnrollmentDevices(full?.matchDevices);

  if (inView && full && activeDevices.length === 0) {
    return (
      <Anchor
        component="button"
        type="button"
        fz={14}
        fw={500}
        c={POC_YELLOW}
        style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3 }}
        onClick={() => openPatientChart(patient.id, 'devices')}>
        Not set
      </Anchor>
    );
  }

  if (activeDevices.length > 0) {
    return (
      <Stack ref={ref} gap={4}>
        {activeDevices.map((device) => (
          <DeviceHoverCard key={device.id} device={hoverSource(device)} />
        ))}
      </Stack>
    );
  }

  const reportDevice =
    patient.lastTransmission?.device ??
    patient.currentReport?.device ??
    patient.previousReport?.device ??
    null;
  const label = [vendorDisplayName(reportDevice?.vendor), reportDevice?.type]
    .filter(Boolean)
    .join(' ');
  if (!label) {
    return (
      <Text ref={ref} className={styles.muted}>
        —
      </Text>
    );
  }
  return (
    <Text ref={ref} className={styles.cell}>
      {label}
    </Text>
  );
};
